<template>
  <BcSheet :title="row.name"
           @close="$emit('close')">
    <div class="cu-sheet-meta">
      <CustomerChip :label="row.chipTh"
                    :tracked="!!row.tracked"
                    :ordered="!!row.ordered" />
      <span>{{ metaLine }}</span>
      <span class="cu-page">{{ row.pageName }}</span>
    </div>
    <Spinner v-if="state === 'loading'"
             size="sm" />
    <div v-else-if="state === 'error'"
         class="alert alert-warning d-flex align-items-center flex-wrap mb-0 unavailable">
      <span><i class="fal fa-exclamation-triangle mr-1"></i>{{ errorText }}</span>
      <button type="button"
              class="btn btn-outline-primary btn-sm ml-auto"
              @click="load">{{ $t("messenger.retry") }}</button>
    </div>
    <template v-else>
      <div ref="chat"
           class="cu-chat">
        <div v-for="(m, i) in messages"
             :key="i"
             class="cu-msg"
             :class="m.fromPage ? '--page' : '--buyer'">
          <span v-if="m.text || m.imageUrl"
                class="cu-bub">
            <img v-if="m.imageUrl"
                 :src="m.imageUrl"
                 class="cu-img"
                 alt=""
                 loading="lazy" />
            <template v-if="m.text">{{ m.text }}</template>
          </span>
          <span class="cu-mono cu-time">{{ clock(m.at) }}</span>
        </div>
      </div>
      <a v-if="inboxUrl"
         class="btn btn-primary btn-lg w-100 mt-3"
         :href="inboxUrl"
         target="_blank"
         rel="noopener">{{ $t("broadcast.customers.open_chat") }}</a>
    </template>
  </BcSheet>
</template>

<script>
import BcSheet from "@/components/derived/broadcast/BcSheet";
import CustomerChip from "@/components/derived/broadcast/CustomerChip";
import Spinner from "@/components/core/Spinner";
import { customerMeta, clock } from "@/components/derived/broadcast/customerTime";

// Read-only chat of one customer, live from the API: buyer left, Page right, newest at the bottom.
// `row` is a list entry; the title, chip and meta line come from it, the bubbles from fetchCustomerMessages.
// Errors show the API's messageTh (else the Messenger "unavailable" line) with a retry, like the Pages card.
export default {
  name: "CustomerChatSheet",
  components: { BcSheet, CustomerChip, Spinner },
  props: {
    row: { type: Object, required: true },
  },
  data() {
    return { state: "loading", messages: [], inboxUrl: null, messageTh: null };
  },
  computed: {
    metaLine() {
      return customerMeta(this.row.inboundCount, this.row.lastInboundAt);
    },
    errorText() {
      return this.messageTh || this.$t("messenger.unavailable");
    },
  },
  created() {
    this.load();
  },
  methods: {
    clock,
    load() {
      this.state = "loading";
      this.$store.dispatch("broadcast/fetchCustomerMessages", {
        customerId: this.row.customerId,
        pageId: this.row.pageId,
        callback: (ok, data) => {
          if (this._isDestroyed) return;
          if (!ok) {
            this.messageTh = (data && data.messageTh) || null;
            this.state = "error";
            return;
          }
          this.messages = Array.isArray(data.messages) ? data.messages : [];
          this.inboxUrl = data.inboxUrl || null;
          this.state = "ready";
          this.$nextTick(this.toBottom);
        },
      });
    },
    toBottom() {
      const el = this.$refs.chat;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
      // images change the height once loaded
      el.querySelectorAll("img").forEach((img) => img.addEventListener("load", () => (el.scrollTop = el.scrollHeight)));
    },
  },
};
</script>

<style lang="scss" scoped>
.cu-sheet-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.6rem;
  color: #555;
  font-size: 0.9375rem;
  margin-bottom: 0.75rem;
}

.cu-page {
  color: #767676;
  font-size: 0.8125rem;
}

.cu-chat {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 45vh;
  overflow-y: auto;
  padding: 0.75rem;
  background: #f3f3f3;
  border-radius: 10px;
}

.cu-msg {
  display: flex;
  flex-direction: column;
  max-width: 80%;

  &.--buyer {
    align-self: flex-start;
    align-items: flex-start;
  }

  &.--page {
    align-self: flex-end;
    align-items: flex-end;
  }
}

.cu-bub {
  padding: 0.5rem 0.8rem;
  border-radius: 16px;
  background: #fff;
  color: #333;
  font-size: 0.9375rem;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.cu-msg.--page .cu-bub {
  background: $brand-blue-mid;
  color: #fff;
}

.cu-img {
  display: block;
  max-width: 100%;
  max-height: 220px;
  border-radius: 10px;
  margin: 0 0 0.25rem;
}

.cu-time {
  font-size: 0.6875rem;
  color: #767676;
  margin-top: 0.15rem;
}

.cu-mono {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-variant-numeric: tabular-nums;
}

body.--dark {
  .cu-sheet-meta {
    color: #c4c4c4;
  }

  .cu-page,
  .cu-time {
    color: #a8a8a8;
  }

  .cu-chat {
    background: #4b4b4b;
  }

  .cu-bub {
    background: #373737;
    color: #e6e6e6;
  }

  .cu-msg.--page .cu-bub {
    background: $brand-blue-mid;
    color: #fff;
  }
}
</style>

